'use client'

import React, { useMemo, useState } from 'react'
import Image from 'next/image'
import { Container, Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Button } from '@quark/button'
import isMobile from 'ismobilejs'
import { Reveal, MultiReveal, Animations } from '@quark/animation'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import BusinessModals from '@components/BusinessModal/businessModals'
import RightArrowIcon from '@public/images/enterprise/right-up-arrow.svg'
import styles from './hero.module.scss'

interface StorefrontHeroProps {
  isMobile?: boolean
  heroImage?: {
    mime?: string
    url?: string
  }
  mobileHeroImage?: {
    mime?: string
    url?: string
  }
  tagline?: string
  title?: string
  subtitle?: string
  primaryActionText?: string
  secondaryActionText?: string
  redirectionLink?: string
  formSnippet?: string
  slug?: string
}

const StorefrontHero = (data: StorefrontHeroProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobileDevice = isMobile()?.phone
  const heroMedia = useMemo(
    () => (isMobileDevice ? data?.mobileHeroImage : data?.heroImage) || data?.heroImage || data?.mobileHeroImage,
    [data?.heroImage, data?.mobileHeroImage, isMobileDevice]
  )
  const isVideoMedia = heroMedia?.mime?.includes('video') || false

  return (
    <Container type="fluid">
      <div id="spline-start-position" className={styles.imageWrapper}>
        <PropertyControlledComponent controllerProperty={isVideoMedia} fallback={null}>
          <video
            className={styles.backgroundVideo}
            src={heroMedia?.url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        </PropertyControlledComponent>
        <PropertyControlledComponent controllerProperty={!isVideoMedia && !!heroMedia?.url} fallback={null}>
          <Image
            src={heroMedia?.url || ''}
            alt=""
            fill
            priority
            sizes="100vw"
            className={styles.backgroundImage}
            style={{ objectFit: 'cover' }}
          />
        </PropertyControlledComponent>
        <Container
          type="max-parent"
          paddingTop={{ lg: 'spacing-144', md: 'spacing-120', sm: 'spacing-104' }}
          paddingBottom={{ lg: 'spacing-160', md: 'spacing-120', sm: 'spacing-104' }}
          paddingLeft="spacing-16"
          paddingRight="spacing-16"
          style={{ position: 'relative', zIndex: 1 }}>
          <Flex flexDirection="column" alignItems="center" justifyContent="center">
            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
              <Container type="max-parent" paddingBottom="spacing-8">
                <Text
                  as="p"
                  fontStyle={{ lg: 'um_text-xl/normal', md: 'um_text-lg/normal', sm: 'um_text-base/normal' }}
                  textAlign={{ lg: 'center', md: 'center', sm: 'center' }}
                  color="accent/textBrand-default">
                  <PropertyControlledComponent controllerProperty={data?.tagline} fallback={null}>
                    {data?.tagline}
                  </PropertyControlledComponent>
                </Text>
              </Container>
            </Reveal>
            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
              <Text
                as="h1"
                fontStyle={{ lg: 'um_text-6xl/normal', md: 'um_text-4xl/normal', sm: 'um_text-3xl/normal' }}
                color="base/primary"
                textAlign={{ lg: 'center', md: 'center', sm: 'center' }}>
                <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
              </Text>
            </Reveal>

            <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 300 }}>
              <Container type="max-parent" paddingTop="spacing-12" paddingBottom="spacing-32">
                <Text
                  as="p"
                  fontStyle={{ lg: 'um_text-xl/normal', md: 'um_text-base/normal', sm: 'um_text-sm/normal' }}
                  color="base/secondary"
                  textAlign={{ lg: 'left', md: 'center', sm: 'center' }}>
                  {data?.subtitle}
                </Text>
              </Container>
            </Reveal>
            <Container type="max-parent" paddingBottom="spacing-160">
              <MultiReveal
                as={Flex}
                gap={{
                  lg: 'spacing-16',
                  md: 'spacing-16',
                  sm: 'spacing-12',
                }}
                flexDirection={{
                  lg: 'row',
                  md: 'row',
                  sm: 'column',
                }}
                justifyContent="center"
                alignItems="center"
                animationDelays={(i: number) => 400 + i * 100}
                animationProps={{ animation: Animations.FadeIn50px }}>
                <PropertyControlledComponent controllerProperty={data?.primaryActionText} fallback={null}>
                  <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
                    <div className={styles.buttonWrapper}>
                      <Button type="solid" size={{ lg: 'lg', md: 'md', sm: 'md' }} onClick={() => setIsModalOpen(true)}>
                        <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                          {data?.primaryActionText}
                        </Text>
                      </Button>
                    </div>
                  </Reveal>
                </PropertyControlledComponent>
                <PropertyControlledComponent controllerProperty={data?.secondaryActionText} fallback={null}>
                  <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 400 }}>
                    <div className={styles.buttonWrapper}>
                      <Button
                        type="outline"
                        size={{ lg: 'lg', md: 'md', sm: 'md' }}
                        onClick={() => data?.redirectionLink && window.open(data?.redirectionLink, '_blank')}>
                        <Container paddingRight="spacing-36">
                          <Text fontStyle="um_text-lg/medium" color="base/primary">
                            {data?.secondaryActionText}
                          </Text>
                          <div className={styles.iconContainer}>
                            <RightArrowIcon width={26} height={26} className={styles.iconAnimation} />
                          </div>
                        </Container>
                        <div className={styles.secondaryShimmer} />
                      </Button>
                    </div>
                  </Reveal>
                </PropertyControlledComponent>
              </MultiReveal>
            </Container>
          </Flex>
        </Container>
      </div>
      <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={data?.formSnippet} />
    </Container>
  )
}
export default StorefrontHero
